import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Save, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { SECTION_LIBRARY, newSection } from './sectionDefs';
import { BasicsTab } from './BasicsTab';
import { SectionsTab } from './SectionsTab';
import PricingEngineManager from '../pricing/PricingEngineManager';
import { EstimatesManager } from '../pricing/EstimatesManager';
import { OwnerBookingsTab } from './OwnerBookingsTab';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  ['basics', 'Basics'],
  ['sections', 'Sections'],
  ['calculator', 'Cost Calculator'],
  ['estimates', 'Estimates & Quotes'],
  ['owner', 'Owner & Bookings']
];

export default function VenuePageBuilder({ page: initialPage, adminToken, onClose, onSaved }) {
  const [page, setPage] = useState(initialPage);
  const [tab, setTab] = useState('basics');
  const [isSaving, setIsSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);

  const update = (patch) => {
    setPage(prev => ({ ...prev, ...patch }));
    setDirty(true);
  };
  const updateHero = (key, v) => update({ hero: { ...(page.hero || {}), [key]: v } });
  const updateCard = (key, v) => update({ card: { ...(page.card || {}), [key]: v } });
  const updateContact = (key, v) => update({ contact: { ...(page.contact || {}), [key]: v } });

  const sections = page.sections || [];
  const setSections = (next) => update({ sections: next });

  const addSection = (type) => {
    const s = newSection(type);
    setSections([...sections, s]);
    setShowAdd(false);
    setExpandedId(s.id);
    setTab('sections');
  };

  const save = async () => {
    setIsSaving(true);
    try {
      const res = await axios.put(`${API_URL}/admin/venue-pages/${page.id}`, page, { params: { admin_token: adminToken } });
      setPage(res.data.page);
      setDirty(false);
      toast.success('Page saved');
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save page');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="venue-page-builder">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-stone-400 font-semibold">Editing Page</p>
          <h2 className="font-serif text-2xl md:text-3xl text-stone-900 mt-1" data-testid="builder-page-name">{page.name}</h2>
          <p className="text-sm text-stone-500 mt-0.5">Public URL: <span className="font-mono text-xs bg-stone-100 px-1.5 py-0.5 rounded">/venues/{page.slug}</span></p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Switch checked={!!page.published} onCheckedChange={v => update({ published: v })} data-testid="builder-published-switch" />
            <span className="text-sm text-stone-600">Published</span>
          </div>
          <a href={`/venues/${page.slug}`} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm"><ExternalLink className="w-4 h-4 mr-1" /> Preview</Button>
          </a>
          <Button size="sm" onClick={save} disabled={isSaving || !dirty} className="bg-stone-900 hover:bg-stone-800" data-testid="builder-save-btn">
            <Save className="w-4 h-4 mr-1" /> {isSaving ? 'Saving…' : dirty ? 'Save' : 'Saved'}
          </Button>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="builder-close-btn"><X className="w-5 h-5" /></Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 flex-wrap border-b border-stone-200 pb-3">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 rounded-full text-xs font-medium transition-colors ${tab === key ? 'bg-[#1A3626] text-white' : 'bg-white border border-stone-200 text-stone-600 hover:border-stone-400'}`}
            data-testid={`builder-tab-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'basics' && (
        <BasicsTab page={page} update={update} updateHero={updateHero} updateCard={updateCard} updateContact={updateContact} />
      )}

      {tab === 'sections' && (
        <SectionsTab
          sections={sections}
          setSections={setSections}
          page={page}
          setPage={setPage}
          setDirty={setDirty}
          expandedId={expandedId}
          setExpandedId={setExpandedId}
          dragIdx={dragIdx}
          setDragIdx={setDragIdx}
          onAddClick={() => setShowAdd(true)}
          goToCalculatorTab={() => setTab('calculator')}
        />
      )}

      {tab === 'calculator' && <PricingEngineManager adminToken={adminToken} venueSlug={page.slug} />}

      {tab === 'estimates' && (
        <div className="bg-white border border-stone-200 rounded-xl p-5">
          <EstimatesManager adminToken={adminToken} venueSlug={page.slug} />
        </div>
      )}

      {tab === 'owner' && <OwnerBookingsTab adminToken={adminToken} venueSlug={page.slug} />}

      {/* Add section dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Add a section</DialogTitle>
            <DialogDescription>Pick a module to add to this page. You can customize and reorder it after adding.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            {SECTION_LIBRARY.map(def => (
              <button
                key={def.type}
                onClick={() => addSection(def.type)}
                className="text-left border border-stone-200 rounded-lg p-4 hover:border-stone-900 hover:bg-stone-50 transition-colors"
                data-testid={`add-section-${def.type}`}
              >
                <p className="font-medium text-stone-900 text-sm">{def.label}</p>
                <p className="text-xs text-stone-500 mt-1">{def.desc}</p>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {dirty && (
        <div className="sticky bottom-4 flex justify-end z-10">
          <Button onClick={save} disabled={isSaving} className="bg-[#C6A87C] text-[#0A1710] hover:bg-[#1A3626] hover:text-white shadow-lg" data-testid="builder-save-floating-btn">
            <Save className="w-4 h-4 mr-2" /> {isSaving ? 'Saving…' : 'Save page'}
          </Button>
        </div>
      )}
    </div>
  );
}
